import React, { createContext, useContext, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

const NavigationBlockerContext = createContext();

export const useBlockNavigation = () => useContext(NavigationBlockerContext);

export const NavigationBlockerProvider = ({ children }) => {
  const navigate = useNavigate();
  const [blocker, setBlocker] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nextPath, setNextPath] = useState(null);

  const allowNavigationRef = useRef(false);

  /**
 * showDialog
 *
 * @summary Handles navigation attempts when a blocker is active, showing a confirmation dialog if needed.
 *
 * @description
 * If navigation blocking is enabled, stores the intended destination and opens a confirmation dialog. If blocking is not enabled, immediately navigates to the specified route.
 *
 * @param {Object} params - The navigation parameters.
 * @param {string} params.to - The target route to navigate to.
 * @param {Object} [params.state] - Optional state to pass to the target route.
 *
 * @returns {void}
 *
 * @example
 * showDialog({ to: "/dashboard", state: { from: "settings" } });
 */
  const showDialog = ({ to, state }) => {
    if (blocker) {
      setNextPath({ to, state });
      setDialogOpen(true);
    } else {
      navigate(to, { state });
    }
  };

  /**
 * confirmNavigation
 *
 * @summary Confirms navigation when a blocker is active, allowing the route change and resetting blocker state.
 *
 * @description
 * Sets the navigation permission flag, closes the confirmation dialog, disables the blocker, and navigates to the stored destination with optional state.
 *
 * @returns {void}
 *
 * @example
 * confirmNavigation();
 */
  const confirmNavigation = () => {
    allowNavigationRef.current = true;
    setDialogOpen(false);
    setBlocker(false);
    const { to, state } = nextPath;
    navigate(to, { state });
  };

  /**
 * cancelNavigation
 *
 * @summary Cancels a pending navigation attempt and resets navigation blocker state.
 *
 * @description
 * Closes the confirmation dialog, clears the next navigation path, and resets the navigation permission flag to prevent unintended route changes.
 *
 * @returns {void}
 *
 * @example
 * cancelNavigation();
 */
  const cancelNavigation = () => {
    setDialogOpen(false);
    setNextPath(null);
    allowNavigationRef.current = false;
  };

  return (
    <NavigationBlockerContext.Provider
      value={{
        setBlocker,
        getBlocker: () => blocker,
        showDialog
      }}
    >
      {children}
      <Dialog open={dialogOpen} onClose={cancelNavigation}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          You have unsaved changes. Are you sure you want to leave this page?
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelNavigation}>Stay</Button>
          <Button onClick={confirmNavigation} color="error">Leave</Button>
        </DialogActions>
      </Dialog>
    </NavigationBlockerContext.Provider>
  );
};
